import React, { Component } from 'react';
import { Divider, Grid, IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import PropTypes from 'prop-types';

import i18n from '../../i18n';
import StringItemSelector from './StringItemSelector';
import GenericDeleteModal from './GenericDeleteModal';
import RequireScope from '../RequireScope';
import {
  addOrigin,
  deleteOrigin,
  fetchOrigins,
  updateOrigin,
} from '../../helpers/externalRequests';
import { mapOriginEntities } from '../../helpers/optionFormatters';
import AddSingleFieldModal from './AddSingleFieldModal';
import EditSingleFieldModal from './EditSingleFieldModal';

class OriginEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedOriginId: 0,
      selectedOriginName: null,
      origins: [],
      originNames: [],
      showDeleteModal: false,
      showEditModal: false,
      showAddModal: false
    };
  }

  showDeleteModal = () => {
    this.setState({ showDeleteModal: true });
  };

  hideDeleteModal = () => {
    this.setState({ showDeleteModal: false });
  };

  showEditModal = () => {
    this.setState({ showEditModal: true });
  };

  hideEditModal = () => {
    this.setState({ showEditModal: false });
  };

  showAddModal = () => {
    this.setState({ showAddModal: true });
  };

  hideAddModal = () => {
    this.setState({ showAddModal: false });
  };

  fetchOrigins = async () => {
    const origins = await fetchOrigins();
    const originNames = origins.data.origins.map(origin => origin.originName);
    this.setState({
      origins: origins.data.origins.map(mapOriginEntities),
      originNames }
    );
  };

  createOrigin = async (originName) => {
    const { openSnackbar } = this.props;
    try {
      const response = await addOrigin({ originName });
      this.hideAddModal();
      openSnackbar(
        `${i18n.t('setting:successOriginCreate')}: ${
          response.data.originName
        }`
      );
      this.fetchOrigins();
    } catch (error) {
      openSnackbar(`${i18n.t('setting:errorOriginCreate')}: ${error}`);
    }
  };

  saveOrigin = async (updatedOriginName) => {
    const { selectedOriginId } = this.state;
    const { openSnackbar } = this.props;
    try {
      const response = await updateOrigin(selectedOriginId, { originName: updatedOriginName });
      this.hideEditModal();
      if (response.status === 200) {
        openSnackbar(i18n.t('setting:successOriginUpdate'));
        this.fetchOrigins();
      } else {
        openSnackbar(i18n.t('setting:errorOriginUpdate'));
      }
    } catch (error) {
        openSnackbar(`${i18n.t('setting:errorOriginUpdate')} ${error}`);
    }
  };

  deleteOrigin = async (selectedOriginId) => {
    const { openSnackbar } = this.props;
    try {
      await deleteOrigin(selectedOriginId);
      this.hideDeleteModal();
      openSnackbar(i18n.t('setting:successOriginDelete'));
      this.setState({ selectedOriginName: null, selectedOriginId: 0 }, () =>
        this.fetchOrigins()
      );
    } catch {
      openSnackbar(i18n.t('setting:errorOriginDelete'));
    }
  };

  componentDidMount = () => {
    this.fetchOrigins();
  };

  onOriginChange = event => {
    const { value } = event.target;
    if (value) {
      this.setState(prevState => {
        const selectedOriginId = value;
        const selectedOrigin = prevState.origins.find(
          origin => origin.id === value
        );
        return {
          selectedOriginName: selectedOrigin.label,
          selectedOriginId
        };
      });
    }
  };

  render() {
    const {
      selectedOriginId,
      selectedOriginName,
      origins,
      originNames,
      showEditModal,
      showDeleteModal
    } = this.state;

    const { showAddModal } = this.state;

    return (
      <Grid container>
        <Grid item xs={7}>
          <h6>{i18n.t('setting:manageOrigins')}</h6>
          <Divider />
          <br />
          <StringItemSelector
            selectedItem={selectedOriginId}
            items={origins}
            onItemChange={this.onOriginChange}
            label={i18n.t('setting:chooseOrigin')}
            helperText={i18n.t('setting:originHelp')}
          />
          <RequireScope scopes={['entities:write']}>
            <IconButton
              color="secondary"
              aria-label="add"
              onClick={this.showAddModal}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              aria-label="edit"
              color="secondary"
              onClick={this.showEditModal}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              aria-label="delete"
              color="secondary"
              onClick={this.showDeleteModal}
            >
              <DeleteIcon />
            </IconButton>
          </RequireScope> 
        </Grid>
        <AddSingleFieldModal
          confirmMessage={i18n.t('setting:addOriginConfirm')}
          instruction={i18n.t('setting:enterOrigin')}
          fieldLabel={i18n.t('setting:originName')}
          fieldName="OriginAddTextField"
          showModal={showAddModal}
          onAddField={this.createOrigin}
          onClose={this.hideAddModal}
          existingFieldNames={originNames}
        />
        <EditSingleFieldModal
          selectedItem={selectedOriginName}
          showModal={showEditModal}
          title={i18n.t('setting:editOriginTitle')}
          onClose={this.hideEditModal}
          onSave={this.saveOrigin}
          existingFieldNames={originNames}
          warningText={i18n.t('setting:warningOriginModal')}
        />
        <GenericDeleteModal
          showModal={showDeleteModal}
          noSelectionTitle={i18n.t('setting:warningOriginModal')}
          confirmMessage={`${i18n.t(
            'setting:confirmDeleteOrigin'
          )}: ${selectedOriginName}?`}
          selectedId={selectedOriginId}
          onClose={this.hideDeleteModal}
          onDelete={this.deleteOrigin}
        />
      </Grid>
    );
  }
}

OriginEditor.propTypes = {
  openSnackbar: PropTypes.func.isRequired,
};

export default OriginEditor;
